<template>
    <div class="ml-5 mr-5 mt-5"  id="importEvent">
        <v-row>
            <v-col cols="12" class="text-left">
                <h3>{{ $t("page.importEvent.title") }}</h3>
                <p class="text-left mb-0">{{ $t("page.importEvent.description_1") }}</p>
            </v-col>
        </v-row> 
        <v-row>
            <v-col
                class="d-flex"
                cols="12" sm="3" md="2"
            >
                <v-select
                    v-model="year"
                    :items="years"
                    :label="$t('common.year')"
                    :no-data-text="$t('common.no_data')"
                ></v-select>
            </v-col>
        </v-row> 
        <v-row>
            <v-col sm="10" cols="12">
                <VueTabulator ref="tabulator" v-model="tabulatorData" :options="tabulatorOptions" />
            </v-col>
        </v-row>
        <Notification 
            :dialog="showNot" 
            :title="$t('page.importEvent.title_confirm', { eventName: $escapeHtml.decode(eventName) })"
            @cancel="cancelConfirm"
            @confirm="confirmImport"
            :persistent="persistent"
            :loadingImport="loadingImport"
            :disabledImport="disabledImport"
        >
            <p class="text-left mt-2">{{ $t('page.importEvent.races_to_import') }}</p>
            <div v-if="bListRaces">
                <v-select 
                    v-model="racesToImport"
                    :items="eventSelected.races" 
                    item-text="raceName" item-value="id"
                    :label="$t('page.importEvent.races')" 
                    multiple outlined dense
                    :menu-props="{ top: true, offsetY: true, contentClass: 'denseList' }"
                    :no-data-text="$t('common.no_data')"
                >
                    <template v-slot:item="{ item }">
                            <span>{{ item.id }} :: {{ item.raceName }}</span>
                    </template>
                    <template v-slot:selection="{ item, index }">
                        <v-chip v-if="index == 0" small>
                            <span>{{ item.raceName }}</span>
                        </v-chip>
                        <span v-if="racesToImport.length > 1 && index === 0" class="grey--text caption">
                            (+{{ racesToImport.length - 1 }} others)
                        </span>
                    </template>
                </v-select>
            </div>

            <p class="text-left mt-2">{{$t("page.importEvent.description_2", { eventName })}}</p>
            <v-checkbox
                v-model="oom"
                :label="$t('page.importEvent.description_check_oom', { eventName })"
                :disabled="disabledImport"
            ></v-checkbox>
        </Notification>
        <SGSNotificationAlert 
            :snackbar="alert" :notificationMessage="msgAlert" @cancel="closeAlert" 
            :type="typeAlert" :right="true" :top="true" class="alert"
        />
    </div>
</template>

<script>
import { apiCalls } from "@/mixins/apiCalls.js";
import Notification from "@/components/custom/notification/Notification.vue";
import SGSNotificationAlert from "@/components/custom/notification/Alert.vue";

export default {
    mixins: [apiCalls],
    components: { Notification, SGSNotificationAlert },
    data() {
        return {
            tabulatorData: [],
            showNot: false,
            eventSelected: null,
            oom: false,
            persistent: true,
            loadingImport: false,
            disabledImport: false,
            alert: false,
            msgAlert: '',
            typeAlert: 'warning',
            modelTest: null,
            racesToImport: [],
            year: null,
            years: []
        }
    },
    computed: {
        eventName() {
            if(this.eventSelected) return this.eventSelected.event;
            return "";
        },
        bListRaces()
        {
            return this.eventSelected && this.eventSelected.races;
        },
        tabulatorOptions() {
            return {
                layout: "fitColumns",
                placeholder: this.$t("common.table.no-data"),
                height: 500,
                printAsHtml: true,
                downloadRowRange: "all",
                layoutColumnsOnNewData: true,
                initialSort: [{column:"eventDate", dir:"desc"}],
                columns: [
                    {
                        title: this.$t("page.importEvent.id"),
                        field: "eid",
                        sorter: "string",
                        headerFilter: "input",
                        headerFilterPlaceholder: " ",
                        minWidth: 50,
                        width: 60,
                        formatter: "textarea"
                    },
                    {
                        title: this.$t("page.importEvent.promoter"),
                        field: "promoter",
                        sorter: "string",
                        headerFilterPlaceholder: " ",
                        headerFilter: 'input',
                        minWidth: 100,
                        formatter: "textarea"
                    },
                    {
                        title: this.$t("page.importEvent.event"),
                        field: "event",
                        sorter: "string",
                        headerFilter: "input",
                        headerFilterPlaceholder: " ",
                        minWidth: 100,
                        formatter: "textarea"
                    },
                    {
                        title: this.$t("page.importEvent.event_date"),
                        field: "eventDate",
                        sorter: "string",
                        headerFilter: this.inputDateHeaderFilter,
                        headerFilterPlaceholder: "yyyy-mm-dd",
                        minWidth: 160,
                        width: 160,
                        formatter: "textarea"
                    },
                    {
                        title: this.$t("page.importEvent.end_date"),
                        field: "eventEndDate",
                        sorter: "string",
                        headerFilter: this.inputDateHeaderFilter,
                        headerFilterPlaceholder: "yyyy-mm-dd",
                        minWidth: 160,
                        width: 160,
                        formatter: "textarea"
                    },
                    {
                        title: this.$t("page.importEvent.import"),
                        field: "action",
                        minWidth: 60,
                        width: 110,
                        hozAlign: "center",
                        vertAlign: "center",
                        headerSort: false,
                        formatter: function () {
                            return "<i class='far fa-download py-2' style='font-size: 2em;'></i>";
                        },
                        cellClick: function (e, cell) {
                            const data = cell.getData();
                            this.eventSelected = data;
                            this.showNot = true;
                            this.oom = false;
                            this.racesToImport = [];
                        }.bind(this)
                    }
                ],
                dataLoaded: function () {},
            };
        }
    },
    watch: {
        year() {
            this.getEvents();
        }
    },
    methods: {
        async getEvents() {
            const response = await this.getApi(`/api/v1/promoter/importEvent/getEventsImport/${this.year}`);
            
            if(response.success) {
                this.tabulatorData = response.message.map((el) => {
                    let races = el.races.map((race) => {
                        return {
                            ...race,
                            raceName: this.$escapeHtml.decode(race.raceName)
                        };
                    });
                    return {
                        ...el,
                        event: this.$escapeHtml.decode(el.event),
                        race: this.$escapeHtml.decode(el.race),
                        promoter: this.$escapeHtml.decode(el.promoter),
                        races
                    };
                });
            }
        },
        cancelConfirm() {
            this.showNot = false;
            this.oom = false;
            const parent = this;
            this.$nextTick(()=>{
                parent.eventSelected = null;
            })
        },
        async confirmImport() {
            if(this.eventSelected) {
                this.loadingImport = this.disabledImport = this.persistent = true;
                const response = await this.postApi(`/api/v1/promoter/importEvent/${this.eventSelected.eid}`, { 
                    oom: this.oom,
                    racesToImport: this.racesToImport
                });
                if(response.success) {
                    this.showAlert(this.$t("page.importEvent.import_success"), 'success');
                    this.cancelConfirm();
                    if(this.$route.name == 'ImportEvent') {
                        window.location.href = '/admin/promoterDashboard'; 
                    } else {
                        this.getEvents();
                    }
                } else {
                    if(response.message == 2) {
                        this.showAlert(this.$t("page.importEvent.no_members_event"), 'error');
                    } else {
                        this.showAlert(this.$t("page.importEvent.import_failed"), 'error');
                    }
                }
                this.loadingImport = this.disabledImport = this.persistent = false;
            } else {
                this.showAlert(this.$t("page.importEvent.not_event_selected"), 'error');
            }

        },
        closeAlert() {
            this.alert = false;
            const parent = this;
            setTimeout(function() {
                parent.msgAlert = '';
                parent.typeAlert = 'warning';
            },1000);
        },
        showAlert(msg, type) {
            this.alert = true;
            this.msgAlert = msg;
            this.typeAlert = type;
        } ,
        clickDateText() {
            document.getElementById('dateInput').focus();
        },
        raceInfo(race){
            return race.hasParticipants ? race.raceName : `${race.raceName} (${this.$t("page.importEvent.hasNoParticipants")})`;
        },
        clickDate() {},
        inputDateHeaderFilter() {

            var input = document.createElement("INPUT");
            input.setAttribute("type", "text");
            input.style = 'width: 100%';
            input.style.padding = "4px";
            const keyEvent = ($e)  => {
                if(
                    ($e.key != "0" && $e.key != "1" && $e.key != "2" && $e.key != "3" && $e.key != "4" && $e.key != "5" && $e.key != "6" && $e.key != "7" && 
                    $e.key != "8" && $e.key != "9" && $e.key != "Backspace" && $e.key != "-") || (input.value.length >= 10 && $e.key != "Backspace")
                ) {
                    $e.preventDefault();
                } else {
                    if(
                        (input.value.length < 4 || (input.value.length > 4 && input.value.length < 7) || (input.value.length > 7 && input.value.length < 10)) && 
                        $e.key != "0" && $e.key != "1" && $e.key != "2" && $e.key != "3" && $e.key != "4" && $e.key != "5" && $e.key != "6" && $e.key != "7" && 
                        $e.key != "8" && $e.key != "9" && $e.key != "Backspace"
                    ) {
                        $e.preventDefault();
                    } else if((input.value.length == 4 || input.value.length == 7) && $e.key != "-" && $e.key != "Backspace"){
                        $e.preventDefault();
                    } else if(input.value.length >= 10 && $e.key != "Backspace") {
                        $e.preventDefault();
                    }
                }
            };
            input.addEventListener("keydown", keyEvent);

            return input;
        },
    },
    mounted() {
        const currentDate = new Date();
        const currentYear = currentDate.getFullYear();
        const minYear = currentYear - 2;
        this.year = currentYear;

        for(var year = currentYear; year >= minYear; year--)
        {
            this.years.push(year);
        }
    }
}
</script>

<style lang="scss">
#importEvent {
    .tabulator[tabulator-layout=fitColumns] .tabulator-row .tabulator-cell:last-of-type {
        background-color: transparent !important;
    }
}
.denseList .v-select-list .v-list-item__action {
    margin-top: 0px;
    margin-bottom: 0px;
}
</style>